import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Martini, Save, Loader2, Download, Search, Users, Clock, Eye, EyeOff, CheckCircle2,
} from 'lucide-react';
import api from '../../api/client';
import { useToast } from '../../hooks/use-toast';
import { Button } from '../../components/ui/button';

/**
 * AdminAperitivoPanel — "Aperitivo Nymia" (Phase 77).
 * Single config drives BOTH landing sections (/join-community, /call)
 * and the transactional emails. Plus the bookings table with search,
 * filters, counters and CSV export.
 */

const PROFESSIONS = ['Psicologo/a', 'Customer Care o Call Center', 'Altro'];

const STATUS_OPTIONS = [
  { value: 'active', label: 'Attivo', desc: 'Form visibile, prenotazioni confermate + email di conferma.', icon: CheckCircle2, color: 'border-emerald-500/50 bg-emerald-500/10 text-emerald-300' },
  { value: 'soldout', label: 'Sold Out', desc: "Form visibile come lista d'attesa (email lista d'attesa).", icon: Clock, color: 'border-amber-500/50 bg-amber-500/10 gold-text' },
  { value: 'hidden', label: 'Nascosto', desc: 'La sezione sparisce da entrambe le landing.', icon: EyeOff, color: 'border-red-500/50 bg-red-500/10 text-red-300' },
];

const FIELDS = [
  { key: 'title', label: 'Titolo' },
  { key: 'subtitle', label: 'Sottotitolo' },
  { key: 'badge', label: 'Badge informativo' },
  { key: 'invite_text', label: "Testo dell'invito", textarea: true, rows: 5 },
  { key: 'venue', label: 'Luogo', half: true },
  { key: 'address', label: 'Indirizzo', half: true },
  { key: 'event_date', label: 'Data', half: true },
  { key: 'phone', label: 'Numero di telefono', half: true },
  { key: 'time_start', label: 'Ora di inizio', half: true },
  { key: 'time_end', label: 'Ora di fine', half: true },
  { key: 'success_message', label: 'Testo mostrato dopo la prenotazione', textarea: true, rows: 4 },
  { key: 'waitlist_message', label: "Testo mostrato dopo l'iscrizione in lista d'attesa", textarea: true, rows: 4 },
  { key: 'email_subject', label: 'Oggetto email di conferma' },
  { key: 'email_body', label: 'Corpo email di conferma', textarea: true, rows: 10 },
  { key: 'waitlist_email_subject', label: "Oggetto email lista d'attesa" },
  { key: 'waitlist_email_body', label: "Corpo email lista d'attesa", textarea: true, rows: 8 },
];

const inputCls = 'w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-amber-500/60';

const AdminAperitivoPanel = () => {
  const { toast } = useToast();
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [bookings, setBookings] = useState({ items: [], total_confirmed: 0, total_waitlist: 0 });
  const [search, setSearch] = useState('');
  const [fProf, setFProf] = useState('');
  const [fStatus, setFStatus] = useState('');
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    api.get('/admin/aperitivo/config').then(({ data }) => setCfg(data)).catch(() => {
      toast({ title: 'Impossibile caricare la configurazione', variant: 'destructive' });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadBookings = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/aperitivo/bookings', {
        params: { search, profession: fProf, status: fStatus },
      });
      setBookings(data);
    } catch (_e) { /* noop */ }
  }, [search, fProf, fStatus]);

  useEffect(() => {
    const tmr = setTimeout(loadBookings, 300);
    return () => clearTimeout(tmr);
  }, [loadBookings]);

  const save = async () => {
    if (!cfg || saving) return;
    setSaving(true);
    try {
      const { data } = await api.put('/admin/aperitivo/config', cfg);
      setCfg(data);
      toast({ title: 'Configurazione salvata', description: 'Le landing e le email sono già aggiornate.' });
    } catch (e) {
      toast({ title: 'Errore nel salvataggio', description: e.response?.data?.detail || '', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get('/admin/aperitivo/bookings/export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'aperitivo_nymia_prenotazioni.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (_e) {
      toast({ title: 'Esportazione non riuscita', variant: 'destructive' });
    } finally { setExporting(false); }
  };

  const setField = (k) => (e) => setCfg((c) => ({ ...c, [k]: e.target.value }));

  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' }); } catch (_e) { return iso; }
  };

  const rows = useMemo(() => bookings.items || [], [bookings]);

  if (!cfg) {
    return <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin gold-text" /></div>;
  }

  return (
    <div className="space-y-8" data-testid="admin-aperitivo-panel">
      <header className="flex items-center gap-2">
        <Martini className="h-5 w-5 gold-text" />
        <h2 className="text-lg font-bold">Aperitivo Nymia</h2>
        <span className="text-xs text-muted-foreground">— sezione condivisa su tutte le landing: /, /creator, /join-community, /call, /cosa-puoi-fare</span>
      </header>

      {/* ── Stato prenotazioni ── */}
      <section className="rounded-xl border border-border bg-card p-4">
        <h3 className="mb-3 text-sm font-semibold">Stato delle prenotazioni</h3>
        <div className="grid gap-3 md:grid-cols-3">
          {STATUS_OPTIONS.map((o) => (
            <button
              key={o.value}
              onClick={() => setCfg((c) => ({ ...c, status: o.value }))}
              className={`rounded-xl border p-3.5 text-left transition-colors ${cfg.status === o.value ? o.color : 'border-border bg-background hover:border-white/25'}`}
              data-testid={`aperitivo-status-${o.value}`}
            >
              <span className="flex items-center gap-2 text-sm font-bold">
                <o.icon className="h-4 w-4" /> {o.label}
                {cfg.status === o.value && <span className="ml-auto text-[10px] font-bold uppercase">attuale</span>}
              </span>
              <p className="mt-1 text-xs opacity-80">{o.desc}</p>
            </button>
          ))}
        </div>
      </section>

      {/* ── Contenuti ── */}
      <section className="rounded-xl border border-border bg-card p-4">
        <h3 className="mb-3 text-sm font-semibold">Contenuti sezione & email</h3>
        <p className="mb-4 text-xs text-muted-foreground">
          Nei testi email puoi usare i segnaposto: {'{nome} {luogo} {indirizzo} {data} {ora_inizio} {ora_fine} {telefono}'}
        </p>
        <div className="grid gap-4 md:grid-cols-2">
          {FIELDS.map((f) => (
            <div key={f.key} className={f.half ? '' : 'md:col-span-2'}>
              <label className="mb-1 block text-xs font-semibold text-muted-foreground">{f.label}</label>
              {f.textarea ? (
                <textarea rows={f.rows || 4} value={cfg[f.key] || ''} onChange={setField(f.key)} className={inputCls} data-testid={`aperitivo-field-${f.key}`} />
              ) : (
                <input type="text" value={cfg[f.key] || ''} onChange={setField(f.key)} className={inputCls} data-testid={`aperitivo-field-${f.key}`} />
              )}
            </div>
          ))}
        </div>
        <Button onClick={save} disabled={saving} className="mt-4 gold-gradient text-black" data-testid="aperitivo-save-btn">
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Salva modifiche
        </Button>
      </section>

      {/* ── Prenotazioni ── */}
      <section className="rounded-xl border border-border bg-card p-4">
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <h3 className="flex items-center gap-1.5 text-sm font-semibold"><Users className="h-4 w-4" /> Prenotazioni</h3>
          <span className="rounded-full bg-emerald-500/15 px-2.5 py-0.5 text-[11px] font-bold text-emerald-300" data-testid="aperitivo-count-confirmed">
            Confermati: {bookings.total_confirmed}
          </span>
          <span className="rounded-full bg-amber-500/15 px-2.5 py-0.5 text-[11px] font-bold gold-text" data-testid="aperitivo-count-waitlist">
            Lista d'attesa: {bookings.total_waitlist}
          </span>
          <Button onClick={exportCsv} disabled={exporting} variant="outline" size="sm" className="ml-auto gap-1.5" data-testid="aperitivo-export-csv-btn">
            {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} Esporta CSV
          </Button>
        </div>

        <div className="mb-3 flex flex-wrap gap-2">
          <div className="relative min-w-[220px] flex-1">
            <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Cerca per nome o email…"
              className={`${inputCls} pl-9`} data-testid="aperitivo-search-input"
            />
          </div>
          <select value={fProf} onChange={(e) => setFProf(e.target.value)} className={`${inputCls} w-auto`} data-testid="aperitivo-filter-profession">
            <option value="">Tutte le professioni</option>
            {PROFESSIONS.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <select value={fStatus} onChange={(e) => setFStatus(e.target.value)} className={`${inputCls} w-auto`} data-testid="aperitivo-filter-status">
            <option value="">Tutti gli stati</option>
            <option value="confirmed">Confermato</option>
            <option value="waitlist">Lista d'attesa</option>
          </select>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs" data-testid="aperitivo-bookings-table">
            <thead>
              <tr className="border-b border-border text-muted-foreground">
                <th className="py-2 pr-3 font-semibold">Data e ora</th>
                <th className="py-2 pr-3 font-semibold">Nome e Cognome</th>
                <th className="py-2 pr-3 font-semibold">Email</th>
                <th className="py-2 pr-3 font-semibold">Telefono</th>
                <th className="py-2 pr-3 font-semibold">Professione</th>
                <th className="py-2 pr-3 font-semibold">Landing</th>
                <th className="py-2 font-semibold">Stato</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={7} className="py-6 text-center text-muted-foreground">Nessuna prenotazione trovata.</td></tr>
              ) : rows.map((b) => (
                <tr key={b.id} className="border-b border-border/50" data-testid={`aperitivo-booking-row-${b.id}`}>
                  <td className="py-2 pr-3 whitespace-nowrap">{fmtDate(b.created_at)}</td>
                  <td className="py-2 pr-3 font-semibold">{b.full_name}</td>
                  <td className="py-2 pr-3">{b.email}</td>
                  <td className="py-2 pr-3">{b.phone || '—'}</td>
                  <td className="py-2 pr-3">{b.profession}</td>
                  <td className="py-2 pr-3">/{b.source}</td>
                  <td className="py-2">
                    {b.status === 'confirmed'
                      ? <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 font-bold text-emerald-300">Confermato</span>
                      : <span className="rounded-full bg-amber-500/15 px-2 py-0.5 font-bold gold-text">Lista d'attesa</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      <span className="hidden"><Eye className="h-3 w-3" /></span>
    </div>
  );
};

export default AdminAperitivoPanel;
